import { legacy } from '@/shared/api/client';
import type {
  ApiAudienceOption,
  ApiCampaignAction,
  ApiCampaignBase,
  ApiChannel,
  ApiEnvelope,
} from './types';

/**
 * Chamadas que e-mail e SMS repetem igual, trocando só o prefixo da rota
 * (`/email-marketing`, `/sms-marketing`). Tudo passa pelo proxy autenticado
 * do BFF (`/bff/api/...`), com `X-Passbook-Id` para a autorização por loja.
 * O que muda por canal (templates, identidades, números) mora no `api/` dele.
 */

export function passbookHeaders(idPassbook: string) {
  return { headers: { 'X-Passbook-Id': idPassbook } };
}

export function channelPath(channel: ApiChannel): string {
  return `/${channel}-marketing`;
}

export function unwrap<T>(envelope: ApiEnvelope<T> | T): T {
  if (envelope && typeof envelope === 'object' && 'data' in (envelope as object)) {
    return (envelope as ApiEnvelope<T>).data;
  }
  return envelope as T;
}

export async function listCampaigns<T extends ApiCampaignBase>(channel: ApiChannel, idPassbook: string): Promise<T[]> {
  const res = await legacy.get<ApiEnvelope<T[]>>(
    `${channelPath(channel)}/${encodeURIComponent(idPassbook)}/campaigns`,
    passbookHeaders(idPassbook),
  );
  const rows = unwrap(res);
  return Array.isArray(rows) ? rows : [];
}

export async function getCampaign<T extends ApiCampaignBase>(
  channel: ApiChannel,
  id: string,
  idPassbook: string,
  paging: { page: number; pageSize: number },
): Promise<T> {
  const query = new URLSearchParams({ page: String(paging.page), pageSize: String(paging.pageSize) });
  const res = await legacy.get<ApiEnvelope<T>>(
    `${channelPath(channel)}/campaigns/${encodeURIComponent(id)}?${query.toString()}`,
    passbookHeaders(idPassbook),
  );
  return unwrap(res);
}

export async function approveCampaign<T extends ApiCampaignBase>(
  channel: ApiChannel,
  idPassbook: string,
  body: unknown,
): Promise<{ campaign: T; leftOut: number }> {
  const res = await legacy.post<ApiEnvelope<{ campaign: T; leftOut?: number }>>(
    `${channelPath(channel)}/${encodeURIComponent(idPassbook)}/campaigns/approve`,
    body,
    passbookHeaders(idPassbook),
  );
  const data = unwrap(res);
  return { campaign: data.campaign, leftOut: typeof data.leftOut === 'number' ? data.leftOut : 0 };
}

export async function runCampaignAction<T extends ApiCampaignBase>(
  channel: ApiChannel,
  id: string,
  action: ApiCampaignAction,
  idPassbook: string,
): Promise<T> {
  const res = await legacy.post<ApiEnvelope<{ campaign: T }>>(
    `${channelPath(channel)}/campaigns/${encodeURIComponent(id)}/${action}`,
    undefined,
    passbookHeaders(idPassbook),
  );
  return unwrap(res).campaign;
}

export async function deleteCampaign(channel: ApiChannel, id: string, idPassbook: string): Promise<void> {
  await legacy.delete<ApiEnvelope<{ deleted: boolean }>>(
    `${channelPath(channel)}/campaigns/${encodeURIComponent(id)}`,
    passbookHeaders(idPassbook),
  );
}

export async function listAudiences(channel: ApiChannel, idPassbook: string): Promise<ApiAudienceOption[]> {
  const res = await legacy.get<ApiEnvelope<ApiAudienceOption[]>>(
    `${channelPath(channel)}/${encodeURIComponent(idPassbook)}/audiences`,
    passbookHeaders(idPassbook),
  );
  const rows = unwrap(res);
  return Array.isArray(rows) ? rows : [];
}

/** Chaves do React Query. Tudo de uma loja num canal cai sob `scope`. */
export const marketingKeys = {
  scope: (channel: ApiChannel, idPassbook: string) => ['marketing', channel, idPassbook] as const,
  campaigns: (channel: ApiChannel, idPassbook: string) => ['marketing', channel, idPassbook, 'campaigns'] as const,
  /** Prefixo de todas as páginas do detalhe de uma campanha (para invalidar de uma vez). */
  campaignAll: (channel: ApiChannel, idPassbook: string, id: string) =>
    ['marketing', channel, idPassbook, 'campaign', id] as const,
  campaign: (channel: ApiChannel, idPassbook: string, id: string, page: number) =>
    ['marketing', channel, idPassbook, 'campaign', id, page] as const,
  audiences: (channel: ApiChannel, idPassbook: string) => ['marketing', channel, idPassbook, 'audiences'] as const,
  templates: (idPassbook: string) => ['marketing', 'email', idPassbook, 'templates'] as const,
  identities: (idPassbook: string) => ['marketing', 'email', idPassbook, 'identities'] as const,
  numbers: (idPassbook: string) => ['marketing', 'sms', idPassbook, 'numbers'] as const,
};
